import { useState } from 'react';
import { Box, Grid, TextField, Typography, FormLabel, Button, } from '@mui/material';

import { register as registerUser } from '../context/actions/auth/register';
import '../css/contactform.scss';

import { isValidPostal, normalizePostal } from '../utils/postal';

export interface IContactForm {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    fromAddress: string;
    postalCode: string;
    toAddress: string;
    startDate: string;
    nbAdults: number;
    nbChildren: number;
    nbBabies: number;
    nbChildSeats: number;
    nbGrandeValises: number;
    nbValisesCabine: number;
    nbGolfBags: number;
    comment: string;
    // Vol aller
    flightDate: string;
    startFlight: string;
    // Vol retour
    backDate: string;
    backFlight: string;
    backTime: string;
    fromFlight: string;
    vatNumber: string;
}

const defaultContactForm = {
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    fromAddress: "",
    postalCode: "",
    toAddress: "",
    startDate: "",
    nbAdults: 0,
    nbChildren: 0,
    nbBabies: 0,
    nbChildSeats: 0,
    nbGrandeValises: 0,
    nbValisesCabine: 0,
    nbGolfBags: 0,
    comment: "",
    // Vol aller
    flightDate: "",
    startFlight: "",
    // Vol retour
    backDate: "",
    backFlight: "",
    backTime: "",
    fromFlight: "",
    vatNumber: "",
}

export default function ContactFormMUI() {

    const [form, setForm] = useState<IContactForm>(defaultContactForm);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setForm({ ...form, [e.currentTarget.id]: e.currentTarget.value });
    };

    // Garde ta regex si tu veux; ici version simple
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const hasEmailError = !!form.emailAddress && !pattern.test(form.emailAddress);
    const hasPostalError = !isValidPostal(form.postalCode);
    const emailError = (): string => {
        if (!form.emailAddress) return '';
        if (!pattern.test(form.emailAddress)) return 'The email is not valid';
        return ' ';
    };

    const postalError = (): string => {
        if (!form.postalCode || form.postalCode.trim() === "") return POSTAL_ERROR_MSG;
        if (!isValidPostal(form.postalCode)) return POSTAL_ERROR_MSG;
        return " ";
    };
    const POSTAL_ERROR_MSG = 'Veuillez entrer un code postal valide (4 ou 5 chiffres, entre 1000 et 99999).';


    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        console.log('form', form);
        if (hasEmailError || hasPostalError) {
            if (hasEmailError) {
                alert('Veuillez entrer une adresse e-mail dans le formulaire avant de le soumettre.');
            }
            if (hasPostalError) {
                alert('Veeuillez fournir un code postal de 4 ou 5 chiffres.');
            }
        }
        else {
            // Normalisations pour l’envoi
            const payload = {
                ...form,
                postalCode: normalizePostal(form.postalCode), // string de chiffres
            };

            registerUser(payload);
            alert('Votre demande de réservation a été envoyée avec succès. Vous recevrez bientôt un e-mail avec le bon de commande.');
        }
    };

    return (
        <Box
            component="form"
            method="post"
            noValidate
            onSubmit={(e) => onSubmit(e)}
            className="contact-form content-width"
            sx={{
                mx: 'auto',
                p: { xs: 2, sm: 2 },
                maxWidth: 1200,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                '& .MuiFormLabel-asterisk': { color: 'red' }
            }}
        >
            <Typography variant="h4" component="h2">
                Envoyez-nous un message…
            </Typography>

            {/* Groupe : Informations personnelles */}
            <Box className="contact-form-group" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" component="h3">
                    Informations personnelles
                </Typography>

                {/* Nom et prénom */}
                <Box className="contact-form-name contact-form-item" sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                    <FormLabel>
                        Nom et prénom
                        <Typography component="span" color="error" aria-hidden="true">
                            {' '}*
                        </Typography>
                    </FormLabel>
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="firstName"
                                name="firstName"
                                label="Prénom"
                                placeholder="Prénom"
                                value={form.firstName ?? ''}
                                onChange={onChange}
                                autoComplete="given-name"
                                variant="outlined"
                                size="medium"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="lastName"
                                name="lastName"
                                label="Nom"
                                placeholder="Nom"
                                value={form.lastName ?? ''}
                                onChange={onChange}
                                autoComplete="family-name"
                                variant="outlined"
                                size="medium"
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Adresse e-mail */}
                <Box className="contact-form-email contact-form-item">
                    <TextField
                        fullWidth
                        required
                        type="email"
                        id="emailAddress"
                        name="emailAddress"
                        label="Adresse e-mail"
                        value={form.emailAddress ?? ''}
                        onChange={onChange}
                        autoComplete="email"
                        variant="outlined"
                        size="medium"
                        error={Boolean(hasEmailError)}
                        helperText={hasEmailError ? emailError() : ' '}
                        aria-describedby={hasEmailError ? 'emailError' : undefined}
                    />
                </Box>

                {/* Numéro de téléphone */}
                <Box className="contact-form-phone contact-form-item">
                    <TextField
                        fullWidth
                        required
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        label="Numéro de téléphone"
                        value={form.phoneNumber ?? ''}
                        onChange={onChange}
                        autoComplete="tel"
                        variant="outlined"
                        size="medium"
                    />
                </Box>
            </Box>

            {/* Groupe : Informations pour la prise en charge */}
            <Box className="contact-form-group" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" component="h3">
                    Informations pour la prise en charge
                </Typography>

                <Box className="flex contact-form-item flex-wrap numbers" sx={{ gap: 2 }}>
                    <Grid container>
                        <Grid container size={{ xs: 8 }} spacing={2} alignItems="flex-start">
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box className="contact-form-nb-adults">
                                    <TextField
                                        id="nbAdults"
                                        name="nbAdults"
                                        type="text"
                                        className="form-control"
                                        value={form.nbAdults}
                                        onChange={onChange}
                                        fullWidth
                                        label="Nombre d'adultes"
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box className="contact-form-nb-children">
                                    <TextField
                                        id="nbChildren"
                                        name="nbChildren"
                                        type="text"
                                        className="form-control"
                                        value={form.nbChildren}
                                        onChange={onChange}
                                        fullWidth
                                        label="Nombre d'enfants"
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box className="contact-form-nb-babies">
                                    <TextField
                                        id="nbBabies"
                                        name="nbBabies"
                                        type="text"
                                        className="form-control"
                                        value={form.nbBabies}
                                        onChange={onChange}
                                        fullWidth
                                        label="Nombre de sièges bébés (maxi cosi)"
                                    />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Box className="contact-form-nb-childseats">
                                    <TextField
                                        id="nbChildSeats"
                                        name="nbChildSeats"
                                        type="text"
                                        className="form-control"
                                        value={form.nbChildSeats}
                                        onChange={onChange}
                                        fullWidth
                                        label="Nombre de sièges enfants (réhausseurs)"
                                    />
                                </Box>
                            </Grid>
                            <Typography>
                                2 sièges enfants (réhausseurs) sont mis à votre disposition :
                            </Typography>
                        </Grid>

                        {/* Image */}
                        <Box
                            component="img"
                            className="contact-form-siege-enfant"
                            src="/images/cars/Siège enfant.png"
                            alt="Siège enfant"
                            sx={{ maxHeight: 300 }}>
                        </Box>
                    </Grid>
                </Box>

                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box className="contact-form-nb-grandes-valises">
                            <TextField
                                id="nbGrandeValises"
                                name="nbGrandeValises"
                                type="text"
                                className="form-control"
                                value={form.nbGrandeValises}
                                onChange={onChange}
                                fullWidth
                                label="Nombre de grandes valises"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box className="contact-form-nb-valises-cabine">
                            <TextField
                                id="nbValisesCabine"
                                name="nbValisesCabine"
                                type="text"
                                className="form-control"
                                value={form.nbValisesCabine}
                                onChange={onChange}
                                fullWidth
                                label="Nombre de valises de cabine"
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Box className="contact-form-nb-golfbags">
                            <TextField
                                id="nbGolfBags"
                                name="nbGolfBags"
                                type="text"
                                className="form-control"
                                value={form.nbGolfBags}
                                onChange={onChange}
                                fullWidth
                                label="Nombre de sacs de golf"
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Groupe : Informations pour le trajet aller */}
            <Box className="contact-form-group" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" component="h3">
                    Informations pour le trajet aller
                </Typography>

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            id="fromAddress"
                            name="fromAddress"
                            autoComplete="address"
                            type="text"
                            className="form-control"
                            value={form.fromAddress}
                            onChange={onChange}
                            fullWidth
                            label="Adresse de départ"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            id="postalCode"
                            name="postalCode"
                            autoComplete="postal-code"
                            type="text"
                            className="form-control"
                            value={form.postalCode}
                            onChange={onChange}
                            fullWidth
                            label="Code postal de départ"
                            error={Boolean(hasPostalError)}
                            helperText={hasPostalError ? postalError() : " "}
                            aria-describedby={hasPostalError ? "postalError" : undefined}
                            inputProps={{
                                inputMode: "numeric",        // clavier numérique sur mobile
                                pattern: "\\d{4,5}",         // indice HTML (mais on garde notre validation JS)
                                maxLength: 5,
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            required
                            id="toAddress"
                            name="toAddress"
                            autoComplete="destination"
                            type="text"
                            className="form-control"
                            value={form.toAddress}
                            onChange={onChange}
                            fullWidth
                            label="Adresse de destination"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            id="startDate"
                            name="startDate"
                            type="text"
                            className="form-control"
                            value={form.startDate}
                            onChange={onChange}
                            fullWidth
                            label="Jour et heure de prise en charge à domicile"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Groupe : Vols aller/retour */}
            <Box className="contact-form-group" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={16} alignItems="flex-start">
                    <Grid container size={{ xs: 12, sm: 6 }} spacing={2} alignItems="flex-start">
                        <Typography variant="h6" component="h3">Informations pour le vol aller</Typography>
                        <TextField
                            id="flightDate"
                            name="flightDate"
                            type="text"
                            className="form-control"
                            value={form.flightDate}
                            onChange={onChange}
                            fullWidth
                            label="Jour et heure du vol aller"
                        />
                        <TextField
                            id="startFlight"
                            name="startFlight"
                            type="text"
                            className="form-control"
                            value={form.startFlight}
                            onChange={onChange}
                            fullWidth
                            label="Numéro de vol départ"
                        />
                    </Grid>
                    <Grid container size={{ xs: 12, sm: 6 }} spacing={2} alignItems="flex-start">
                        <Typography variant="h6" component="h3">Informations pour le vol retour</Typography>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                id="backDate"
                                name="backDate"
                                type="text"
                                className="form-control"
                                value={form.backDate}
                                onChange={onChange}
                                fullWidth
                                label="Jour et heure du vol retour"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                id="backFlight"
                                name="backFlight"
                                type="text"
                                className="form-control"
                                value={form.backFlight}
                                onChange={onChange}
                                fullWidth
                                label="Numéro de vol retour"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                id="backTime"
                                name="backTime"
                                type="text"
                                className="form-control"
                                value={form.backTime}
                                onChange={onChange}
                                fullWidth
                                label="Heure d'arrivée du vol retour"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                id="fromFlight"
                                name="fromFlight"
                                type="text"
                                className="form-control"
                                value={form.fromFlight}
                                onChange={onChange}
                                fullWidth
                                label="Provenance du vol"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            {/* Groupe : Commentaire */}
            <Box className="contact-form-group contact-form-comment contact-form-item" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" component="h3">Informations supplémentaires</Typography>
                <TextField
                    id="comment"
                    name="comment"
                    className="form-control"
                    value={form.comment}
                    onChange={onChange}
                    fullWidth
                    multiline
                    minRows={4}
                    label="Votre message"
                />
            </Box>

            <Box sx={{
                mt: 1,
                textAlign: 'center'
            }}>
                <Button type="submit" variant="contained">
                    Soumettre
                </Button>
            </Box>
        </Box>
    );
}




//       {/* Submit */}
//       <Box className="contact-form-submit">
//         <Button
//           type="submit"
//           variant="contained"
//           color="secondary"
//           className="button pointer btn btn-secondary"
//         >
//           <span className="text">Soumettre</span>
//                </Button>
//       </Box>
//     </Box>
//   );
